/**
 * The working at each surface a ray meets, written out as a student would
 * (engine target §1: every number traceable to a line of working).
 *
 * Refraction is Snell's law with the numbers substituted; total internal
 * reflection is the critical angle and the comparison; a plane mirror is the law
 * of reflection. After a whole block, the relation the syllabus asks for: a
 * prism's δ = i + e − A, a slab's parallel exit and sideways shift.
 */
import type { Analysis, Crossing } from './analyse';
import { WHITE_LIGHT } from './kinds/sources';
import { defOf } from './registry';
import { Part, Ray, World } from './types';
import { deg, len, rad, sub } from './vec';

export interface SurfaceStep {
    /** "The first face of P₁ (air → glass)". */
    title: string;
    lines: string[];
}

export interface RayWorking {
    rayId: string;
    /** What the ray came from: "R₁". */
    source: string;
    steps: SurfaceStep[];
    /** What the whole passage through a block comes to. */
    summary: string[];
}

const a1 = (x: number): string => `${x.toFixed(1)}°`;
const n2 = (x: number): string => x.toFixed(2);

function mediumName(n: number, w: World): string {
    if (Math.abs(n - w.medium) < 1e-9) return Math.abs(n - 1) < 1e-9 ? 'air' : Math.abs(n - 1.33) < 1e-9 ? 'water' : `n = ${n2(n)}`;
    return `glass, n = ${n2(n)}`;
}

const capital = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

export function rayWorking(w: World, analysis: Analysis, ray: Ray): RayWorking {
    const surfaceOf = new Map(analysis.surfaces.map(s => [s.id, s]));
    const partOf = new Map(w.parts.map(p => [p.id, p]));
    const nameOf = (p: Part): string => defOf(p).name(p, w);
    const source = partOf.get(ray.source.partId);
    const steps: SurfaceStep[] = [];

    ray.vertices.forEach((v, k) => {
        if (k === 0 || !v.surfaceId) return;
        const s = surfaceOf.get(v.surfaceId);
        const part = v.partId ? partOf.get(v.partId) : undefined;
        if (!s || !part) return;
        const rule = s.rule;

        if (rule.law === 'refract' && v.incidence !== undefined && v.n1 !== undefined && v.n2 !== undefined) {
            const title = `${capital(s.name)} (${mediumName(v.n1, w)} → ${mediumName(v.n2, w)})`;
            const i = v.incidence;
            if (v.tir) {
                const sinC = v.n2 / v.n1;
                steps.push({
                    title,
                    lines: [
                        `Critical angle: sin C = n₂ / n₁ = ${n2(v.n2)} / ${n2(v.n1)} = ${sinC.toFixed(4)} → C = ${a1(deg(Math.asin(sinC)))}`,
                        `i = ${a1(i)} is greater than C, so no ray is refracted: total internal reflection.`,
                        `It reflects at the same angle: ${a1(i)} from the normal.`,
                    ],
                });
                return;
            }
            if (i < 1e-6) {
                steps.push({ title, lines: ['It meets the face along the normal (i = 0°), so it goes straight on: r = 0°.'] });
                return;
            }
            const sinR = (v.n1 * Math.sin(rad(i))) / v.n2;
            steps.push({
                title,
                lines: [
                    'n₁ sin i = n₂ sin r',
                    `${n2(v.n1)} × sin ${a1(i)} = ${n2(v.n2)} × sin r`,
                    `sin r = ${sinR.toFixed(4)} → r = ${a1(v.refraction as number)}`,
                    v.n2 > v.n1 ? 'Into a denser medium, it bends towards the normal.' : 'Into a rarer medium, it bends away from the normal.',
                ],
            });
            return;
        }

        if (rule.law === 'ideal') {
            if (!Number.isFinite(rule.f) && v.incidence !== undefined) {
                steps.push({
                    title: capital(s.name),
                    lines: [`Angle of reflection = angle of incidence = ${a1(v.incidence)}, measured from the normal.`],
                });
                return;
            }
            const kind = rule.folds ? 'mirror' : 'lens';
            const fText = `${rule.f > 0 ? 'converging' : 'diverging'}, focal length ${Math.abs(rule.f).toFixed(1)} cm`;
            steps.push({
                title: capital(s.name),
                lines: [`A thin ${kind} (${fText}): it turns the ray by ${a1(v.deviation)}, as the ${kind} formula says.`],
            });
            return;
        }

        if (rule.law === 'absorb' || ray.ended === 'absorbed') {
            steps.push({ title: capital(s.name), lines: ['The ray stops here.'] });
        }
    });

    const summary: string[] = [];
    for (const c of analysis.crossings.filter(x => x.rayId === ray.id)) {
        const part = partOf.get(c.partId);
        if (!part) continue;
        summary.push(...blockSummary(part, c, nameOf(part)));
        if (ray.white && !c.tir) summary.push(...spreadSummary(analysis, ray, c));
    }

    return { rayId: ray.id, source: source ? nameOf(source) : '', steps, summary };
}

/** White light through a block: how far red and violet were turned, and the
 *  angle between them — when the block parted them at all. */
function spreadSummary(analysis: Analysis, ray: Ray, c: Crossing): string[] {
    const along = (nm: number): Crossing | undefined => {
        const sibling = analysis.rays.find(r => r.white && r.wavelength === nm
            && r.source.partId === ray.source.partId && r.source.point === ray.source.point);
        return sibling && analysis.crossings.find(x => x.rayId === sibling.id && x.partId === c.partId && !x.tir);
    };
    const [redNm, , violetNm] = WHITE_LIGHT;
    const red = along(redNm), violet = along(violetNm);
    if (!red || !violet) return [];
    const spread = violet.deviation - red.deviation;
    if (Math.abs(spread) < 0.05) return [];
    return [`Red is turned by δ = ${a1(red.deviation)}, violet by ${a1(violet.deviation)}: the angle of dispersion is ${a1(spread)}.`];
}

function blockSummary(part: Part, c: Crossing, name: string): string[] {
    const i = c.entry.incidence, r1 = c.entry.refraction, r2 = c.exit.incidence, e = c.exit.refraction;
    if (i === undefined || r1 === undefined || c.tir) return [];

    if (part.kind === 'prism' && r2 !== undefined && e !== undefined) {
        const A = (part.data as { apex: number }).apex;
        return [
            `Through ${name}: r₁ + r₂ = ${a1(r1)} + ${a1(r2)} = ${a1(r1 + r2)}, the angle of the prism A = ${a1(A)}.`,
            `Deviation δ = i + e − A = ${a1(i)} + ${a1(e)} − ${a1(A)} = ${a1(i + e - A)}.`,
        ];
    }

    if (c.lateral !== undefined) {
        // It crossed two parallel faces, travelling at r to their normal, so the
        // distance between them is the path inside × cos r.
        const t = len(sub(c.exit.at, c.entry.at)) * Math.cos(rad(r1));
        return [
            `Through ${name}: it leaves parallel to how it went in (δ = 0°).`,
            `Sideways shift d = t · sin(i − r) / cos r = ${t.toFixed(2)} × sin ${a1(i - r1)} / cos ${a1(r1)} = ${c.lateral.toFixed(2)} cm.`,
        ];
    }
    return [];
}
